import { COLOR_PALETTE } from '@inflearn/ds-react-next';
import {
  DEFAULT_MAX_IMAGE_SIZE,
  Editor as DSEditor,
  type EditorFeature,
} from '@inflearn/editor-next';
import { useLang } from '@inflearn/i18n-tools';
import { faH3, faH4, faText } from '@inflearn/pro-regular-svg-icons';
import { isNil, map } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { type ComponentProps, forwardRef, useEffect } from 'react';

import { useEditor } from '../../hooks/useEditor';
import useFilePostImageCreateMutation from '../../mutations/useFilePostImageCreateMutation';

interface Props extends Omit<ComponentProps<typeof DSEditor>, 'children' | 'editor'> {
  disabled?: boolean;
  initialValue?: string;
  placeholder?: string;
  onChange?: (body: string) => void;
  autoFocus?: boolean;
}

const Editor = forwardRef<EditorFeature, Props>(
  ({ disabled, initialValue, placeholder, onChange, autoFocus = false, ...props }, ref) => {
    const { t } = useTranslation();
    const { lang } = useLang();
    const { uploadImageForEditor } = useFilePostImageCreateMutation();

    const editorInstance = useEditor({ disabled, initialValue, placeholder, onChange });

    useEffect(
      function mountedEditor() {
        if (!autoFocus) {
          return;
        }
        editorInstance?.commands.focus();
      },
      [autoFocus, editorInstance],
    );

    if (isNil(editorInstance)) {
      return <></>;
    }

    return (
      <DSEditor editor={editorInstance} {...props} ref={ref} lang={lang}>
        <DSEditor.Toolbar>
          <DSEditor.ToolbarFragment showDivider>
            <DSEditor.ToolbarControlsGroup>
              <DSEditor.Bold />
              <DSEditor.Italic />
              <DSEditor.Strike />
              <DSEditor.Link />
              <DSEditor.TextColor colorPaletteOptions={TEXT_COLORS} />
            </DSEditor.ToolbarControlsGroup>
            <DSEditor.ToolbarControlsGroup>
              <DSEditor.Code />
              <DSEditor.Codeblock />
              <DSEditor.Blockquote />
              <DSEditor.Image
                uploadImage={uploadImageForEditor}
                maxImageSize={DEFAULT_MAX_IMAGE_SIZE}
              />
            </DSEditor.ToolbarControlsGroup>
            <DSEditor.ToolbarControlsGroup>
              <DSEditor.BulletList />
              <DSEditor.OrderedList />
              <DSEditor.Divider />
            </DSEditor.ToolbarControlsGroup>
            <DSEditor.ToolbarControlsGroup>
              <DSEditor.Typo
                headingLevels={map(HEADINGS, ({ label, ...rest }) => ({
                  ...rest,
                  label: t(label),
                }))}
              />
            </DSEditor.ToolbarControlsGroup>
          </DSEditor.ToolbarFragment>
        </DSEditor.Toolbar>
        <DSEditor.Content />
      </DSEditor>
    );
  },
);

const TEXT_COLORS = [
  {
    value: COLOR_PALETTE.gray[9],
    label: 'Black',
  },
  {
    value: COLOR_PALETTE.infgreen[6],
    label: 'Green',
  },
  {
    value: COLOR_PALETTE.blue[6],
    label: 'Blue',
  },
  {
    value: COLOR_PALETTE.red[6],
    label: 'Red',
  },
  {
    value: COLOR_PALETTE.gray[6],
    label: 'Gray',
  },
];

// editor-next 4 부터 TypoHeadingLevelOption.icon 이 필수라, DS 기본 룩업과 같은 아이콘을 지정한다
export const HEADINGS = [
  {
    label: '본문_에디터',
    value: 0,
    icon: faText,
  },
  {
    label: '제목_에디터',
    value: 3,
    icon: faH3,
  },
  {
    label: '부제목_에디터',
    value: 4,
    icon: faH4,
  },
] as const;

Editor.displayName = 'Editor';

export default Editor;
